import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { format } from 'date-fns';
import { api } from '../utils/api';

function getDefaultCashIds(paymentMethods, accounts) {
  const cashMethod = paymentMethods.find((m) => m.type === 'CASH');
  const cashAccount = accounts.find((a) => a.type === 'CASH');
  return {
    paymentMethodId: cashMethod?.id || paymentMethods[0]?.id || '',
    accountId: cashAccount?.id || accounts[0]?.id || ''
  };
}

const NewPaymentDialog = ({ open, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    description: '',
    amount: '',
    paymentMethodId: '',
    accountId: ''
  });

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [pmRes, accRes] = await Promise.all([
          api.get('/payment-methods'),
          api.get('/accounts')
        ]);
        const methods = pmRes.data || [];
        const accs = accRes.data || [];
        setPaymentMethods(methods);
        setAccounts(accs);
        const defaults = getDefaultCashIds(methods, accs);
        setForm({
          description: '',
          amount: '',
          paymentMethodId: defaults.paymentMethodId,
          accountId: defaults.accountId
        });
      } catch (err) {
        setError(err.response?.data?.msg || 'Errore nel caricamento dati');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async () => {
    const amount = parseFloat(String(form.amount).replace(',', '.'));
    if (!form.description.trim()) {
      setError('La descrizione è obbligatoria');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Inserisci un importo valido');
      return;
    }
    if (!form.paymentMethodId || !form.accountId) {
      setError('Seleziona metodo di pagamento e conto');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post('/accounting-movements', {
        description: form.description.trim(),
        amount,
        date: format(new Date(), 'yyyy-MM-dd'),
        type: 'OUT',
        paymentMethodId: form.paymentMethodId,
        accountId: form.accountId,
        source: 'manual'
      });
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || err.response?.data?.errors?.[0]?.msg || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nuovo Pagamento</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Descrizione"
                fullWidth
                value={form.description}
                onChange={handleChange('description')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Importo"
                type="number"
                fullWidth
                value={form.amount}
                onChange={handleChange('amount')}
                inputProps={{ min: 0.01, step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Metodo di pagamento</InputLabel>
                <Select
                  label="Metodo di pagamento"
                  value={form.paymentMethodId}
                  onChange={handleChange('paymentMethodId')}
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Conto</InputLabel>
                <Select
                  label="Conto"
                  value={form.accountId}
                  onChange={handleChange('accountId')}
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>{account.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annulla</Button>
        <Button variant="contained" color="error" onClick={handleSubmit} disabled={saving || loading}>
          {saving ? 'Salvataggio...' : 'Registra uscita'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewPaymentDialog;
